import React, { Fragment, PureComponent } from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'

import Message from './Message'
import { Board, Background, BlurBottom, BlurTop, Container, DismissAllButton, Frame } from './Notifications.board.style'
import PromptMessage from './PromptMessage'

const PROMPT_MESSAGE_TTL = 3000

/**
 * Notifications Board
 */
class NotificationsBoard extends PureComponent {
    /**
     * Prop Types
     */
    static propTypes = {
        // eslint-disable-next-line react/no-unused-prop-types
        messages: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                title: PropTypes.string,
                body: PropTypes.string,
                isDismissible: PropTypes.bool,
                callback: PropTypes.func,
            })
        ),
        onClose: PropTypes.func,
        onDismiss: PropTypes.func,
        onDismissAll: PropTypes.func,
        open: PropTypes.bool,
        setDisplay: PropTypes.bool,
    }

    /**
     * Default Props
     */
    static defaultProps = {
        messages: [],
        onClose() {},
        onDismiss() {},
        onDismissAll() {},
        open: false,
    }

    /**
     * Constructor
     * @param {object} props
     */
    constructor(props) {
        super(props)

        this.state = {
            initialized: false,
            messages: [],
            newMessages: [],
            promptMessage: null,
            promptMessageVisible: false,
            withScroll: false,
        }

        // Create refs for children who need it
        this.frameRef = React.createRef()
        this.boardRef = React.createRef()

        // Bind methods to this instance
        this.onClickDismiss = this.onClickDismiss.bind(this)
        this.onClickDismissAll = this.onClickDismissAll.bind(this)
    }

    /**
     * Add to the state the messages from props not present on it
     * @param {object} nextProps
     * @param {object} lastState
     */
    static getDerivedStateFromProps(nextProps, lastState) {
        const { messages: propsMessages } = nextProps
        const { messages: stateMessages, newMessages: oldNewMessages, initialized } = lastState

        const mappedStateMessages = stateMessages.reduce(
            (carry, m) => ({
                ...carry,
                [m.id]: m,
            }),
            {}
        )

        const toAddMessages = propsMessages.filter(({ id }) => !mappedStateMessages[id])

        if (toAddMessages.length) {
            const newState = {
                messages: [...stateMessages, ...toAddMessages],
            }

            if (initialized) {
                newState.newMessages = [...oldNewMessages, ...toAddMessages]
            }

            return newState
        }

        return null
    }

    componentDidMount() {
        this.setState({ initialized: true })
    }

    /**
     * componentDidUpdate
     */
    componentDidUpdate() {
        this.checkIfBoardIsBiggerThanFrame()
        this.selectNewPromptMessage()
    }

    /**
     * On Click dismiss button
     * First, it changes the state to render the animation,
     * Later, it delays the redux action call to remove the message
     * @param {string} id message id
     */
    onClickDismiss(id) {
        const { onDismiss } = this.props
        const { messages } = this.state
        const message = messages.find(({ id: mid }) => mid === id)

        // Stop if the message is not found or not dissmisible
        if (!message || !message.isDismissible) {
            return
        }

        const index = messages.indexOf(message)

        // Stop if the message has not been found
        // eslint-disable-next-line no-bitwise
        if (!~index) {
            return
        }

        this.setState(
            {
                messages: [
                    ...messages.slice(0, index),
                    {
                        ...message,
                        dismissed: true,
                    },
                    ...messages.slice(index + 1, messages.length),
                ],
            },
            () =>
                setTimeout(() => {
                    this.checkIfBoardIsBiggerThanFrame()
                    onDismiss()
                }, 300)
        )
    }

    /**
     * On Click dismiss all button
     * First, it changes the state to render the animations,
     * Later, it delays the redux action call to remove all the messages
     */
    onClickDismissAll() {
        const { onDismissAll } = this.props
        const { messages } = this.state

        this.setState(
            {
                messages: messages.map((m) => {
                    const message = m
                    if (message.isDismissible) {
                        message.dismissed = true
                    }
                    return message
                }),
            },
            () =>
                setTimeout(() => {
                    this.checkIfBoardIsBiggerThanFrame()
                    onDismissAll()
                }, 300)
        )
    }

    /**
     * Check if the scroll is present on messages board
     */
    checkIfBoardIsBiggerThanFrame() {
        const boardHeight = this.boardRef.current.scrollHeight
        const frameHeight = this.frameRef.current.scrollHeight
        this.setState({ withScroll: boardHeight > frameHeight })
    }

    selectNewPromptMessage = () => {
        const { newMessages, promptMessage } = this.state

        if (!newMessages.length) {
            return
        }

        if (promptMessage) {
            return
        }

        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({
            newMessages: newMessages.slice(1, newMessages.length),
            promptMessage: newMessages[0],
            promptMessageVisible: false,
        })
        this.setShowPromptTimeout()
    }

    clearAllPromptTimeout = () => {
        this.clearDropPromptTimeout()
        this.clearHidePromptTimeout()
        this.clearShowPromptTimeout()
    }

    setShowPromptTimeout = () => {
        this.showPromptTimeout = setTimeout(() => {
            this.setState({ promptMessageVisible: true })
            this.setHidePromptTimeout()
        }, 200)
    }

    clearShowPromptTimeout = () => {
        if (this.showPromptTimeout) {
            clearTimeout(this.showPromptTimeout)
        }
    }

    hidePromptMessage = () => {
        this.setState({ promptMessageVisible: false })
        this.setDropPromptTimeout()
    }

    setHidePromptTimeout = () => {
        this.hidePromptTimeout = setTimeout(() => {
            this.setState({ promptMessageVisible: false })
            this.setDropPromptTimeout()
        }, PROMPT_MESSAGE_TTL)
    }

    clearHidePromptTimeout = () => {
        if (this.hidePromptTimeout) {
            clearTimeout(this.hidePromptTimeout)
        }
        this.clearDropPromptTimeout()
    }

    setDropPromptTimeout = () => {
        this.dropPromptTimeout = setTimeout(() => this.setState({ promptMessage: null }), 500)
    }

    clearDropPromptTimeout = () => {
        if (this.dropPromptTimeout) {
            clearTimeout(this.dropPromptTimeout)
        }
    }

    /**
     * Render
     */
    render() {
        const { onClose, open } = this.props
        const { messages, withScroll, promptMessage, promptMessageVisible } = this.state
        const thereIsPromptMessage = !!promptMessage

        return (
            <Fragment>
                <Container open={open}>
                    <Frame ref={this.frameRef}>
                        {withScroll && <BlurTop />}
                        <Board ref={this.boardRef}>
                            {Object.values(
                                messages.map(({ id, title, body, isDismissible, callback, dismissed }) => (
                                    <Message
                                        key={id}
                                        id={id}
                                        body={body}
                                        callback={callback}
                                        dismissed={dismissed}
                                        isDismissible={isDismissible}
                                        onClickDismiss={this.onClickDismiss}
                                        title={title}
                                    />
                                ))
                            )}
                        </Board>
                        {withScroll && <BlurBottom />}
                        {!isEmpty(messages) && <DismissAllButton onClick={this.onClickDismissAll}>Clear</DismissAllButton>}
                    </Frame>
                </Container>
                {open && <Background onClick={onClose} />}
                {thereIsPromptMessage && (
                    <PromptMessage
                        id={promptMessage.id}
                        body={promptMessage.body}
                        dismissed={promptMessage.dismissed}
                        isVisible={promptMessageVisible}
                        onClickDismiss={this.hidePromptMessage}
                        title={promptMessage.title}
                    />
                )}
            </Fragment>
        )
    }
}

export default NotificationsBoard
